/* eslint semi:0 */
import { route } from 'mithril'
import { uniq } from '../../../utils/array'
import { makeSafeFn } from '../../../utils/fn'
import userStore from '../../stores/userStore'
import { adStore } from '../../stores/adStore'
import { getPlatform, path } from '../../../utils/ua'
import { createElement, referringDomain } from '../../../utils/dom'
import { queryString } from '../../../utils/window'
import { AD_FORMATS } from '../../config/adsConfig'
import {
  getPostMediaTypes,
  getIsEmbededPost,
  get as getFromPdo,
} from '../../../application/pageDataObject'
import slideshowStore from '../../stores/slideshowStore'
import { create as makeEmptyObj } from '../../../utils/object'
import { external as externalScript } from '../../../utils/script'
import cooladataScriptConfig from '../../config/cooladataScript'
import AnalyticsBaseService from '../../services/analytics/analyticsBaseService'
import { currentCountryCode } from '../../../providers/geolocationProvider'

const MEDIUMS = {
  EMBEDDED: 'embedded',
  PINTEREX: 'pinterex',
  NONE: null,
}

const AD_UNIT_PROPERTY_MAPPINGS = {
  [AD_FORMATS.Top]: 'ad_0',
  [AD_FORMATS.Side]: 'ad_1',
  [AD_FORMATS.SideSecondary]: 'ad_2',
  [AD_FORMATS['1x1secondary']]: 'ad_3',
  [AD_FORMATS.Bottom]: 'ad_4',
  [AD_FORMATS.Outofpage]: 'ad_5',
  [AD_FORMATS.InArticle]: 'ad_6',
  [AD_FORMATS['1x1']]: 'ad_7',
}

const VIDEO_EVENTS = {
  VIDEO_PLAY: 'video_play',
  PLAYER_LOADED: 'video_player_loaded',
}

const SHOULD_REPORT_AD_LIFECYCLE_EVENTS = {
  REQUEST: false,
  RESPONSE: false,
}

function onCooladataLoad() {
  window.cooladata.init({
    /* eslint-disable camelcase */
    app_key: getFromPdo('config.cooladata.app_key'),
    user_id: (userStore.state.id || userStore.guestId).toString(),
    /* eslint-enable */
  })
}

let cooladata = externalScript.load({ config: cooladataScriptConfig })
cooladata(makeSafeFn(onCooladataLoad))

//NOTICE: this class should be (and is) used only when not in native app
class CooladataService extends AnalyticsBaseService {
  constructor(options = {}) {
    super(options)
  }

  get className() {
    return 'CooladataService'
  }

  booleanToCooladataInt(bool) {
    switch (bool) {
      case undefined:
        return null
      case true:
        return 1
      case false:
        return 0
    }
  }

  pageViewed() {
    this.reportPageView()
  }

  requestingAds({ adType }) {
    if (!SHOULD_REPORT_AD_LIFECYCLE_EVENTS.REQUEST) {
      return this
    }
    const adRequestPayload = {
      ...this.pageViewData,
      ad_unit: adType, // eslint-disable-line camelcase
    }
    return this.trackEvent('requesting_ads', adRequestPayload)
  }

  slotRendered({ adId, size: { width, height }, isEmpty }) {
    if (!SHOULD_REPORT_AD_LIFECYCLE_EVENTS.RESPONSE) {
      return this
    }
    /* eslint-disable camelcase, lines-around-comment, */
    const slotResponsePayload = {
      ...this.pageViewData,
      ad_unit: adId,
      ad_received: Number(!isEmpty),
      ad_format: `${width}x${height}`,
    }
    /* eslint-enable */
    return this.trackEvent('ad_received', slotResponsePayload)
  }

  allSlotsRendered(action) {
    this.state.adsData = action.events
    this.reportAdsMonitization(this.adsMonitizationData)
  }

  adBlockDetectionFinished() {
    if (adStore.isAdblockPresent && adStore.isAdsEnabled && !this.state.allSlotsRendered) {
      this.reportAdsMonitization()
    }
  }

  socialShare(action) {
    let data = this.pageViewData
    data.obj_name = action.type.split(':').pop() // eslint-disable-line camelcase
    this.trackEvent('share_button_click', data)
  }

  // templateReady(action) {
  //   this.reportVideoEvent(VIDEO_EVENTS.PLAYER_LOADED)
  // }

  videoAdStarted(action) {
    this.reportAdsMonitization(action.payload)
    this.reportVideoEvent(VIDEO_EVENTS.VIDEO_PLAY, action.payload)
  }

  videoStartedPlaying(action) {
    this.reportVideoEvent(VIDEO_EVENTS.VIDEO_PLAY, action.payload)
  }

  before(action) {
    //browsi Action before load
    this.trackEvent('browsi_action_before_load', this.pageViewData)
  }

  after(action) {
    //browsi Action after load
    this.trackEvent('browsi_action_after_load', this.pageViewData)
  }

  displayPopup(action) {
    //fresh8 (betway) display popup
    this.trackEvent('betway_bet_now_click', this.pageViewData)
  }

  componentShown(action) {
    this.reportOnce(action, () => {
      this.trackEvent('fb_like_box_show', this.pageViewData)
    })
  }

  closeBox(action) {
    this.trackEvent('fb_like_box_close', this.pageViewData)
  }

  clickedLike(action) {
    this.trackEvent('fb_like_box_like', this.pageViewData)
  }

  reportVideoEvent(eventName, data = {}, reportOnce = true) {
    let args = Object.assign(makeEmptyObj(), this.pageViewData, data)
    if (reportOnce) {
      let actionId = Object.assign(makeEmptyObj(), data, { type: eventName })
      this.reportOnce(actionId, () => {
        this.trackEvent(eventName, args)
      })
    } else {
      this.trackEvent(eventName, args)
    }
  }

  reportAdsMonitization(eventAdditionalArgs) {
    let monitizationEventArgs = Object.assign(this.pageViewData, eventAdditionalArgs)
    this.trackEvent(`ads_monitization`, monitizationEventArgs)
  }

  reportPageView() {
    let layout = getFromPdo('post.layout')
    let prefix = layout && layout !== 'full' ? `${layout}_` : ''
    this.trackEvent(`${prefix}${getFromPdo('page.type')}_pageview`, this.pageViewData)
  }

  get pageViewData() {
    return Object.assign(makeEmptyObj(), this.generalPageViewData, this.specificPageViewData)
  }

  trackEvent(type, args) {
    this.logAnalytics(type, args)
    cooladata(
      makeSafeFn(() => {
        window.cooladata.trackEvent(type, args)
      })
    )
    return this
  }

  get adsMonitizationData() {
    let data = makeEmptyObj({
      /* eslint-disable camelcase */
      ads_requested: adStore.requestedAdsCount,
      /* eslint-enable */
    })

    for (let adUnit in this.state.adsData) {
      let currentAd = this.state.adsData[adUnit]
      let cooladataAdProperty = AD_UNIT_PROPERTY_MAPPINGS[adUnit]
      if (currentAd && cooladataAdProperty) {
        let adValue
        if (currentAd.isEmpty) {
          adValue = 'empty'
        } else if (currentAd.lineItemId && currentAd.creativeId) {
          adValue = `${currentAd.lineItemId}:${currentAd.creativeId}`
        } else if (currentAd.serviceName) {
          adValue = `service:${currentAd.serviceName}`
        }
        data[cooladataAdProperty] = adValue || ''
      }
    }
    return data
  }

  get generalPageViewData() {
    let user = userStore.state
    let externalReferrer = referringDomain()
    if (externalReferrer && externalReferrer.match(document.domain)) {
      externalReferrer = ''
    }

    /* eslint-disable camelcase */
    return {
      user_start_date: userStore.getStartDate(),
      is_new_session: userStore.isNewSession,
      adblock: this.booleanToCooladataInt(adStore.isAdblockPresent),
      geo: currentCountryCode,
      platform: getPlatform(),
      app_type: getPlatform(),
      app_name: getPlatform(),
      ftbpro_user_id: user.id,
      guest_user_id: userStore.guestId.toString(),
      user_locale: user.locale,
      referrer: externalReferrer,
      locale: getFromPdo('page.locale'),
      obj_name: getFromPdo('page.name'),
    }

    /* eslint-enable */
  }

  get specificPageViewData() {
    return this[`${getFromPdo('page.type')}PageViewArgs`]
  }

  get postPageViewArgs() {
    /* eslint-disable camelcase */
    let postArgs = {
      post_id: getFromPdo('post.id'),
      post_title: getFromPdo('post.title'),
      post_author_name: getFromPdo('post.author.name'),
      post_author_id: getFromPdo('post.author.id'),
      post_published_date: parseInt(getFromPdo('post.created_at') || 0, 10) * 1000,
      post_type: getFromPdo('post.type'),
      post_template: getFromPdo('post.template'),
      post_categories: (getFromPdo('post.categories') || []).join(','),
      page_category: 'posts',
      affiliate_id: queryString.a_aid,
      partner_id: path.isEmbedded ? queryString.partner : userStore.state.partnerId,
      medium: this.medium,
      traffic_source: queryString.utm_source,
      campaign: queryString.utm_campaign,
    }

    /* eslint-enable */
    let templateArgs =
      this[`${getFromPdo('post.template')}PostPageViewArgs`] ||
      getFromPdo('post.template_data') ||
      {}

    return Object.assign(postArgs, templateArgs, this.embeddedPostPageViewArgs)
  }

  get slideshowPostPageViewArgs() {
    let templateData = getFromPdo('post.template_data') || {}
    let currentSlideNumber = slideshowStore.currentSlideIndex
    return {
      /* eslint-disable camelcase */
      current_slide_number: currentSlideNumber,
      current_slide_title: route.param('slideId'),
      items_order: templateData.items_order,
      media_type: getPostMediaTypes()[currentSlideNumber],
      media_provider: (templateData.media_provider || [])[currentSlideNumber],
      /* eslint-enable */
    }
  }

  get topxPostPageViewArgs() {
    let templateData = getFromPdo('post.template_data') || {}
    return {
      /* eslint-disable camelcase */
      post_items_number: templateData.post_items_number,
      media_provider: uniq(templateData.media_provider || []).toString(),
      media_type: uniq(getPostMediaTypes()).toString(),
      /* eslint-enable */
    }
  }

  get embeddedPostPageViewArgs() {
    if (path.isEmbedded) {
      let anchor = createElement('a')
      anchor.href = document.referrer || document.location.href
      return {
        /* eslint-disable camelcase */
        post_type: 'embed_posts',
        embedded_conf: queryString,
        embeding_url: anchor.href,
        /* eslint-enable */
      }
    }
    return {}
  }

  get medium() {
    let qs = queryString
    if (qs.a_aid || qs.af) {
      return MEDIUMS.PINTEREX
    }
    if (qs.utm_medium) {
      return qs.utm_medium
    }
    if (getIsEmbededPost()) {
      return MEDIUMS.EMBEDDED
    }
    return MEDIUMS.NONE
  }
}

export default CooladataService



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/services/analytics/cooladataService.js